/** Micropython stream item types from firmware/SDK (DID 0x24 stream definitions). */
export const MICROPYTHON_STREAM_TYPES = ['int', 'float', 'sensor_float', 'bool', 'electronic_float', 'electronic_bool', 'electronic_uint8'] as const;

export type MicropythonStreamType = (typeof MICROPYTHON_STREAM_TYPES)[number];

/** Wire enum for `micropython_type` in stream item definitions (SDK 0x24). */
export const MICROPYTHON_TYPE_BY_BYTE: Record<number, MicropythonStreamType> = {
  0: 'int',
  1: 'float',
  2: 'sensor_float',
  3: 'bool',
  4: 'electronic_float',
  5: 'electronic_bool',
  6: 'electronic_uint8',
};

export const BYTE_BY_MICROPYTHON_TYPE: Record<MicropythonStreamType, number> = {
  int: 0,
  float: 1,
  sensor_float: 2,
  bool: 3,
  electronic_float: 4,
  electronic_bool: 5,
  electronic_uint8: 6,
};

export const STREAM_KNOWN_MICROPYTHON_TYPES = new Set<string>(MICROPYTHON_STREAM_TYPES);

export function micropythonTypeFromByte(typeByte: number): MicropythonStreamType | 'unknown' {
  return MICROPYTHON_TYPE_BY_BYTE[typeByte & 0xff] ?? 'unknown';
}

export function isOneByteMicropythonStreamType(type: string): boolean {
  return type === 'bool' || type === 'electronic_bool' || type === 'electronic_uint8';
}

export function isBoolMicropythonStreamType(type: string): boolean {
  return type === 'bool' || type === 'electronic_bool';
}

export function micropythonStreamValueByteSize(micropythonType: string | undefined): number | null {
  if (!micropythonType || !STREAM_KNOWN_MICROPYTHON_TYPES.has(micropythonType)) {
    return null;
  }
  return isOneByteMicropythonStreamType(micropythonType) ? 1 : 4;
}

/** Parses one stream value from a payload slice using the definition type. */
export function parseMicropythonStreamValue(view: DataView, uint8: Uint8Array, byteOffset: number, micropythonType: string): number {
  if (isBoolMicropythonStreamType(micropythonType)) {
    return uint8[byteOffset] ? 1 : 0;
  }
  if (micropythonType === 'electronic_uint8') {
    return uint8[byteOffset] ?? 0;
  }
  if (micropythonType === 'int') {
    return view.getInt32(byteOffset, false);
  }
  return view.getFloat32(byteOffset, false);
}

/** Framework `streamData.type` values that map to float motor/sensor widgets. */
export function isFrameworkFloatStreamType(type: string): boolean {
  return type === 'float' || type === 'electronic_float';
}

/** Framework `streamData.type` values that map to boolean motor/sensor widgets. */
export function isFrameworkBoolStreamType(type: string): boolean {
  return type === 'bool' || type === 'electronic_bool';
}

/** Encodes FW `streamItemData` bytes (type byte + null-terminated name per item). */
export function encodeFirmwareStreamItemData(items: ReadonlyArray<{ name: string; micropythonType: string }>): number[] {
  const bytes: number[] = [];
  for (const item of items) {
    const typeByte = BYTE_BY_MICROPYTHON_TYPE[item.micropythonType as MicropythonStreamType];
    bytes.push(typeByte ?? BYTE_BY_MICROPYTHON_TYPE.sensor_float);
    for (const ch of item.name) {
      bytes.push(ch.charCodeAt(0));
    }
    bytes.push(0);
  }
  return bytes;
}
